import React, { useEffect, useState } from 'react'
import GetAppIcon from '@material-ui/icons/GetApp'
import { CircularProgress, IconButton } from '@material-ui/core'
import { Text } from '@tremor/react'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { DonutTagChart } from 'features/Main/NewDashboard/components/DonutTagChart'
import { montserratStyle } from 'features/Main/NewDashboard/styles'
import CustomTooltip from 'features/Main/NewDashboard/components/CustomTooltip'
import { formatDonutChartData } from 'utils/chart'

import { useLoadingTracker } from '../../../hooks/useLoadingTracker'
import { getAgeAnalysisByLevelDetailed, AgeData } from './api'

const ageColors = ['teal', 'sky', 'green', 'emerald', 'lime', 'amber']
const emptyDataColor = ['gray']

interface AgeDonutChartsByLevelProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const AgeDonutChartsByLevel: React.FC<AgeDonutChartsByLevelProps> = ({
  period,
  storeId,
  trigger,
  onLoaded,
}) => {
  const [data, setData] = useState<AgeData[] | null>(null)
  const [statsCall, statsLoading] = useApiCall<
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    { startDate: string; endDate: string; storeId?: number },
    AgeData[]
  >(getAgeAnalysisByLevelDetailed)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'AgeDonutChartsByLevel' })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const startDate = moment()
          .year(period.year)
          .month(period.month)
          .startOf('month')
          .format('YYYY-MM-DD')
        const endDate = moment()
          .year(period.year)
          .month(period.month)
          .endOf('month')
          .format('YYYY-MM-DD')
        const response = await statsCall({ startDate, endDate, storeId })
        if (response) setData(response)
      } finally {
        if (onLoaded) {
          onLoaded()
        }
      }
    }
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  if (statsLoading || !data) {
    return (
      <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-row justify-between items-center mb-6">
          <h3 className="text-base font-bold" style={montserratStyle}>
            Apertura por edad por nivel
          </h3>
          <IconButton disabled={statsLoading} className="ml-2" aria-label="download">
            <GetAppIcon />
          </IconButton>
        </div>
        <div className="flex flex-row gap-8">
          <div className="flex-1 flex items-center justify-center">
            {statsLoading ? (
              <CircularProgress size={40} />
            ) : (
              <Text>No hay datos disponibles para mostrar en este momento.</Text>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Agrupar datos por nivel
  const levels = Array.from(new Set(data.map(item => item.level))).sort()
  const byLevel = levels.map(level => {
    const levelData = data.filter(item => item.level === level)
    // Ordenar segmentos de mayor a menor según cantidad
    const orderedData = [...levelData].sort((a, b) => b.value - a.value)

    const isEmptyData =
      orderedData.length === 1 && orderedData[0].value === 0 && orderedData[0].code === 'empty'
    const colorsToUse = isEmptyData ? emptyDataColor : ageColors

    return {
      level,
      data: formatDonutChartData(
        orderedData.map((item, i) => ({
          ...item,
          color: colorsToUse[i % colorsToUse.length],
        })),
      ),
    }
  })

  const downloadAgeByLevelCSV = () => {
    const rows: string[][] = []
    byLevel.forEach(levelData => {
      rows.push([`Nivel ${levelData.level}`])
      rows.push(['Categoría', 'Proporción (%)'])
      levelData.data.forEach(row => {
        rows.push([row.name, row.value !== undefined ? row.value.toFixed(2) : ''])
      })
      rows.push([''])
    })
    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'edad_por_nivel.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
      <div className="flex flex-row justify-between items-center mb-6">
        <h3 className="text-base font-bold" style={montserratStyle}>
          Apertura por edad por nivel
        </h3>
        <IconButton
          disabled={statsLoading}
          className="ml-2"
          aria-label="download"
          onClick={downloadAgeByLevelCSV}
        >
          <GetAppIcon />
        </IconButton>
      </div>
      <div className="flex flex-row gap-8">
        {byLevel.length > 0 ? (
          byLevel.map(levelData => (
            <div key={`nivel-${levelData.level}`} className="flex-1">
              <DonutTagChart
                title=""
                chartTitle={`Nivel ${levelData.level}`}
                data={levelData.data}
                customTooltip={CustomTooltip}
              />
            </div>
          ))
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default AgeDonutChartsByLevel
